import { useApplicantAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  ESignDocument,
  Link,
  Loader,
  Modal,
  PrimaryButton,
  Text,
} from "@certn/cascadia-react";
import { useContract } from "@certn/cascadia-react/miscellaneous/contract/hooks";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CLICKWRAP_ACCESS_ID } from "../constants";
import { ApplicationContext } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { createESignDownloadHandler } from "../../esign/createESignDownloadHandler";
import { useESignSubmit } from "../../esign/useESignSubmit";

type BiometricConsentResponse =
  components["schemas"]["BiometricConsentTemplateResponse"];

function useBiometricConsent(contractId: string) {
  const { caseId } = useApplicationFormContext();
  const { customerName } = useContext(ApplicationContext);
  const { enableSuperdocApplicantAgreementTemplates } = useFlags();

  const { renderData, contractIDs } = useMemo(
    () => ({
      renderData: { company_name: customerName },
      contractIDs: [contractId],
    }),
    [customerName, contractId]
  );

  const {
    data: contractsData,
    getContractHTML,
    isError: isContractError,
    isAgreed,
  } = useContract({
    accessID: CLICKWRAP_ACCESS_ID,
    contractIDs,
    signerID: caseId,
    renderData,
  });

  const {
    data: biometricConsentData,
    isLoading: isBiometricConsentLoading,
    error: biometricConsentError,
    mutate: mutateBiometricConsent,
  } = useApplicantAPI(
    enableSuperdocApplicantAgreementTemplates
      ? (`/api/internal/applicant/case/${caseId}/biometric-consent/` as "/api/internal/applicant/case/{id}/biometric-consent/")
      : null
  );

  const consentData = biometricConsentData as
    | BiometricConsentResponse
    | undefined;

  const useSuperdoc =
    enableSuperdocApplicantAgreementTemplates && !!consentData?.esign?.url;

  const biometricConsentRequestFailed =
    enableSuperdocApplicantAgreementTemplates &&
    !isBiometricConsentLoading &&
    !!biometricConsentError;

  return {
    contractsData,
    getContractHTML,
    isContractError,
    isAgreed,
    consentData,
    useSuperdoc,
    enableSuperdocApplicantAgreementTemplates,
    isBiometricConsentLoading,
    biometricConsentRequestFailed,
    mutateBiometricConsent,
  };
}

interface Props {
  isOpen: boolean;
  contractId: string;
  onConsent: () => Promise<void>;
  onCancel: () => void;
}

export function BiometricConsentModal({
  isOpen,
  contractId,
  onConsent,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const { caseId } = useApplicationFormContext();

  const {
    contractsData,
    getContractHTML,
    isContractError,
    consentData,
    useSuperdoc,
    enableSuperdocApplicantAgreementTemplates,
    isBiometricConsentLoading,
    biometricConsentRequestFailed,
    mutateBiometricConsent,
  } = useBiometricConsent(contractId);

  const templateId = consentData?.id;
  const awaitingSuperdocTemplate =
    enableSuperdocApplicantAgreementTemplates && isBiometricConsentLoading;
  const showPactSafeClickwrap =
    !useSuperdoc && !awaitingSuperdocTemplate && !biometricConsentRequestFailed;

  // In SuperDoc mode, consent is captured by the drawn-signature submission to
  // the agreement-signature endpoint. Success advances the OneID flow via the
  // parent `onConsent()`; the parent then closes the modal.
  //
  // `onConsent()` is awaited so ESignDocument does not mark the document as
  // agreed (disabling the pad) until `openVerification()` has finished. If that
  // chain throws, `useSubmit` routes it to `onFailure` (Sentry) rather than
  // leaving the pad disabled behind an open modal.
  const { submitSignature } = useESignSubmit(caseId, {
    throwErrors: true,
    onSuccess: async () => {
      await onConsent();
    },
    onFailure: (e) => Sentry.captureException(e),
  });

  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [showScrollError, setShowScrollError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const contractHTML =
    showPactSafeClickwrap &&
    Boolean(contractsData && !isContractError) &&
    getContractHTML(contractId);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (hasScrolledToEnd) return;
      const element = e.target as HTMLDivElement;
      if (
        element.scrollHeight <=
        element.scrollTop + element.clientHeight + 50
      ) {
        setHasScrolledToEnd(true);
        setShowScrollError(false);
      }
    },
    [hasScrolledToEnd]
  );

  const scrollToEnd = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, []);

  const handleAgree = useCallback(async () => {
    if (!hasScrolledToEnd) {
      setShowScrollError(true);
      return;
    }
    setIsSubmitting(true);
    try {
      await onConsent();
    } finally {
      setIsSubmitting(false);
    }
  }, [hasScrolledToEnd, onConsent]);

  const handleClose = useCallback(() => {
    setHasScrolledToEnd(false);
    setShowScrollError(false);
    onCancel();
  }, [onCancel]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog width={useSuperdoc ? 640 : 560}>
        {() => (
          <>
            <DialogHeader onClose={handleClose}>
              {t("Biometric Notice")}
            </DialogHeader>

            <DialogContent padding="space-5">
              {awaitingSuperdocTemplate ? (
                <div className={styles.body}>
                  <Loader type="dots" />
                </div>
              ) : biometricConsentRequestFailed ? (
                <div className={styles.body}>
                  <Text color="text-weak" testID="biometric-consent-api-error">
                    <Trans>Something went wrong</Trans>
                  </Text>
                </div>
              ) : useSuperdoc ? (
                <div className={styles.body}>
                  <ESignDocument
                    data={consentData?.esign}
                    onSubmit={
                      templateId
                        ? (d) => submitSignature(d, templateId)
                        : undefined
                    }
                    onDownload={
                      templateId
                        ? createESignDownloadHandler(caseId, templateId)
                        : undefined
                    }
                    downloadFilename={consentData?.template_name}
                    allowExpand
                    style={{
                      height: "min(72vh, 620px)",
                      minHeight: "440px",
                      width: "100%",
                    }}
                  />
                </div>
              ) : (
                <div className={styles.body}>
                  {contractsData?.contract_data[contractId]?.download_url && (
                    <div className={styles.downloadRow}>
                      <Link
                        testID="biometric-consent-download-link"
                        href={
                          contractsData.contract_data[contractId].download_url
                        }
                        target="_blank"
                        textColor="text-weak"
                        textStyle="form/body-secondary-link"
                      >
                        <Trans>Download</Trans>
                      </Link>
                    </div>
                  )}

                  <div className={styles.scrollContainer}>
                    {isContractError ? (
                      <div
                        ref={contentRef}
                        className={styles.clickwrapContent}
                        aria-label="biometric-notice-content"
                        role="document"
                        tabIndex={0}
                      >
                        <Text
                          color="text-weak"
                          testID="biometric-consent-fetch-error"
                        >
                          <Trans>Something went wrong</Trans>
                        </Text>
                      </div>
                    ) : (
                      <div
                        ref={contentRef}
                        className={styles.clickwrapContent}
                        dangerouslySetInnerHTML={{
                          __html: contractHTML || "",
                        }}
                        aria-label="biometric-notice-content"
                        role="document"
                        tabIndex={0}
                        onScroll={handleScroll}
                      />
                    )}

                    <div className={styles.scrollIndicator}>
                      {hasScrolledToEnd ? (
                        <Text
                          style="global/button-small"
                          testID="end-of-document-indicator"
                        >
                          <Trans>End of document</Trans>
                        </Text>
                      ) : (
                        <button
                          className={styles.scrollToEndButton}
                          onClick={scrollToEnd}
                          data-testid="scroll-to-end-button"
                        >
                          <span className={styles.scrollArrow}>&#8595;</span>
                          <Text style="global/button-small">
                            <Trans>Scroll to end</Trans>
                          </Text>
                        </button>
                      )}
                    </div>
                  </div>

                  <Text
                    style="form/body-secondary"
                    color="text-weak"
                    testID="biometric-consent-acknowledgement"
                  >
                    <Trans>
                      By clicking "Agree and continue," you acknowledge that you
                      have reviewed and consent to the Biometric Notice.
                    </Trans>
                  </Text>

                  {showScrollError && (
                    <Text
                      style="form/body-secondary"
                      color="text-danger"
                      testID="biometric-consent-scroll-error"
                    >
                      <Trans>
                        Please scroll to the end of the Biometric Notice to
                        continue.
                      </Trans>
                    </Text>
                  )}
                </div>
              )}
            </DialogContent>

            <DialogFooter>
              <BasicButton
                testID="biometric-consent-cancel-button"
                onPress={handleClose}
              >
                <Trans>Cancel</Trans>
              </BasicButton>
              {biometricConsentRequestFailed ? (
                <PrimaryButton
                  testID="biometric-consent-retry-button"
                  onPress={() => mutateBiometricConsent()}
                >
                  <Trans>Try again</Trans>
                </PrimaryButton>
              ) : (
                showPactSafeClickwrap && (
                  <PrimaryButton
                    testID="biometric-consent-agree-button"
                    onPress={handleAgree}
                    isLoading={isSubmitting}
                  >
                    <Trans>Agree and continue</Trans>
                  </PrimaryButton>
                )
              )}
            </DialogFooter>
          </>
        )}
      </Dialog>
    </Modal>
  );
}

export function useBiometricConsentStatus(contractId: string) {
  const {
    isAgreed,
    consentData,
    useSuperdoc,
    enableSuperdocApplicantAgreementTemplates,
    isBiometricConsentLoading,
    biometricConsentRequestFailed,
  } = useBiometricConsent(contractId);

  const hasConsentedToCurrentVersion = biometricConsentRequestFailed
    ? false
    : useSuperdoc
      ? !!consentData?.esign?.signedAt
      : enableSuperdocApplicantAgreementTemplates && isBiometricConsentLoading
        ? false
        : isAgreed(contractId);

  // Callers gate the OneID start flow on the SuperDoc *flag* — not derived
  // `useSuperdoc` (which additionally requires esign.url). Using the flag keeps
  // the consent modal mandatory while the template is loading (FP-1161).
  //
  // `superdocEsignAvailable` is for skipping PactSafe after consent: only when
  // esign.url is present (FR-828 PactSafe fallback when it is missing).
  return {
    hasConsentedToCurrentVersion,
    superdocEnabled: enableSuperdocApplicantAgreementTemplates,
    superdocEsignAvailable: useSuperdoc,
  };
}

const styles = {
  body: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  downloadRow: css({
    display: "flex",
    justifyContent: "flex-end",
  }),
  scrollContainer: css({
    border: "1px solid var(--border-base)",
    borderRadius: "var(--border-radius-3)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  }),
  clickwrapContent: css({
    flex: 1,
    overflow: "auto",
    maxHeight: "400px",
    padding: "var(--space-4)",
    "& *": {
      color: "var(--text-weak) !important",
    },
    "& .ps-contract-scroll": {
      maxHeight: "unset !important",
      padding: "0 !important",
      border: "none !important",
    },
  }),
  scrollIndicator: css({
    borderTop: "1px solid var(--border-base)",
    padding: "var(--space-2) var(--space-4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "var(--background-base)",
  }),
  scrollToEndButton: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-1)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "var(--space-2) var(--space-3)",
    borderRadius: "var(--border-radius-2)",
    "&:hover": {
      backgroundColor: "var(--background-hover)",
    },
  }),
  scrollArrow: css({
    fontSize: "14px",
    lineHeight: 1,
  }),
};
